import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import './ds/styles.css';
import './App.css';
import { EASE_OUT } from './ds/motion';
import A2ALandingPage from './pages/A2ALandingPage';

/* Only the homepage ships in the main bundle. Every other route loads its own
   chunk on first visit: /ggs alone carries about 4 MB of inlined map data, and
   the blog brings the whole markdown stack. */
const HomesPrivacyPolicy = React.lazy(() => import('./pages/HomesPrivacyPolicy'));
const HomesTermsOfService = React.lazy(() => import('./pages/HomesTermsOfService'));
const AtaLandingPage = React.lazy(() => import('./pages/AtaLandingPage'));
const ManifestoPage = React.lazy(() => import('./pages/ManifestoPage'));
const GgsLanding = React.lazy(() => import('./__arena/y2-a'));
const BlogIndex = React.lazy(() => import('./pages/BlogIndex'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));

/* The marketing routes cross-fade into each other. The product and legal pages
   are left alone: they own their own scroll and their own first paint.

   Opacity only, deliberately: a transform on the page wrapper would become the
   containing block for the fixed reading-progress bar and pin it to the
   document instead of the viewport. */
function Fade({ children }: { children: React.ReactNode }) {
    const reduced = useReducedMotion();
    if (reduced) return <>{children}</>;
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.16, ease: EASE_OUT } }}
            transition={{ duration: 0.24, ease: EASE_OUT }}
        >
            {children}
        </motion.div>
    );
}

function ScrollToTop() {
    const { pathname } = useLocation();
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function AppRoutes() {
    const location = useLocation();
    return (
        <React.Suspense fallback={null}>
            <ScrollToTop />
            {/* No `initial={false}` here: it reaches every motion component below
                through presence context and would cancel the page's opening
                sequence on first load. */}
            <AnimatePresence mode="wait">
                <Routes location={location} key={location.pathname}>
                    <Route
                        path="/"
                        element={
                            <Fade>
                                <A2ALandingPage />
                            </Fade>
                        }
                    />
                    <Route
                        path="/manifesto"
                        element={
                            <Fade>
                                <ManifestoPage />
                            </Fade>
                        }
                    />
                    <Route path="/ata" element={<AtaLandingPage />} />
                    <Route path="/ggs" element={<GgsLanding />} />
                    <Route
                        path="/blog"
                        element={
                            <Fade>
                                <BlogIndex />
                            </Fade>
                        }
                    />
                    <Route
                        path="/blog/:slug"
                        element={
                            <Fade>
                                <BlogPostPage />
                            </Fade>
                        }
                    />

                    <Route path="/homes/privacy" element={<HomesPrivacyPolicy />} />
                    <Route path="/homes/terms" element={<HomesTermsOfService />} />
                    {/* Retired paths and typos land on the homepage rather than a blank screen. */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </AnimatePresence>
        </React.Suspense>
    );
}

/* ─── APP ─── */
function App() {
    return (
        <Router basename={process.env.PUBLIC_URL || '/'}>
            <AppRoutes />
        </Router>
    );
}

export default App;
